// Mind2Magic CRM — Thunder Staffing Import Module
// Extracted from index.html Step 9.

import { store } from '../store.js'

// ========== THUNDER STAFFING EXCEL IMPORT ==========

function detectThunderStaffing(wb) {
    const results = []
    for (const sheetName of wb.SheetNames) {
        const sn = sheetName.toLowerCase()
        // Skip template and cancellation sheets
        if (sn.includes('template') || sn.includes('cancellation') || sn === 'cancellations') continue
        const ws = wb.Sheets[sheetName]
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null })
        if (!data || data.length < 3) continue

        // Find header row (check rows 0-5)
        let headerRow = -1
        for (let i = 0; i < Math.min(6, data.length); i++) {
            const rowStr = (data[i] || []).map(c => String(c || '').toLowerCase().trim()).join('|')
            if (rowStr.includes('reservation number') || rowStr.includes('nombre del hotel') || rowStr.includes('hotel name')) {
                headerRow = i
                break
            }
        }
        if (headerRow === -1) continue

        const headers = data[headerRow].map(h => String(h || '').trim())
        const headersLower = headers.map(h => h.toLowerCase())
        const joined = headersLower.join(' ')

        let format = null
        // Format C: Staffing Group (has "Nombre del Hotel" or "Valor")
        if (joined.includes('nombre del hotel') || (joined.includes('valor') && joined.includes('travel date'))) {
            format = 'staffing_group'
        }
        // Format A/B: Expedia (has "Client Name" + "Reservation Number" + "Hotel Name")
        else if (joined.includes('client name') && joined.includes('reservation number') && joined.includes('hotel name')) {
            const hasRooms = headersLower.some(h => h.includes('number of room'))
            const hasCommission = headersLower.some(h => h.includes('comision') || h.includes('commission'))
            format = (hasRooms && hasCommission) ? 'expedia_a' : 'expedia_b'
        }

        if (format) {
            results.push({ format, sheetName, data, headers, headersLower, headerRow, hasCommission: headersLower.some(h => h.includes('comision') || h.includes('commission')) })
        }
    }
    return results.length > 0 ? results : null
}

function parseThunderDateRange(text) {
    if (!text) return { checkIn: null, checkOut: null }
    const s = String(text).trim()
        .replace(/Octuber/gi, 'October').replace(/Agust/gi, 'August').replace(/Julu/gi, 'July')
    // Pattern: "August 2 - 4, 2025" (same month)
    const m1 = s.match(/^(\w+)\s+(\d{1,2})\s*-\s*(\d{1,2}),?\s*(\d{4})$/)
    if (m1) return { checkIn: thunderDateParts(m1[1], m1[2], m1[4]), checkOut: thunderDateParts(m1[1], m1[3], m1[4]) }
    // Pattern: "November 30 - December 3, 2025" (cross-month)
    const m2 = s.match(/^(\w+)\s+(\d{1,2})\s*-\s*(\w+)\s+(\d{1,2}),?\s*(\d{4})$/)
    if (m2) return { checkIn: thunderDateParts(m2[1], m2[2], m2[5]), checkOut: thunderDateParts(m2[3], m2[4], m2[5]) }
    // Fallback
    const d = new Date(s)
    if (!isNaN(d) && d.getFullYear() > 2000) return { checkIn: d.toISOString().split('T')[0], checkOut: null }
    return { checkIn: null, checkOut: null }
}

function thunderDateParts(monthStr, day, year) {
    const months = { january:0, february:1, march:2, april:3, may:4, june:5, july:6, august:7, september:8, october:9, november:10, december:11 }
    const m = months[monthStr.toLowerCase()]
    if (m === undefined) return null
    const d = new Date(parseInt(year), m, parseInt(day))
    return isNaN(d) ? null : d.toISOString().split('T')[0]
}

function parseThunderPurchaseDate(text) {
    if (!text) return null
    const s = String(text).trim().replace(/(\d+)(st|nd|rd|th)/gi, '$1')
        .replace(/Octuber/gi, 'October').replace(/Agust/gi, 'August').replace(/Julu/gi, 'July')
    const d = new Date(s)
    if (!isNaN(d) && d.getFullYear() > 2000) return d.toISOString().split('T')[0]
    return null
}

function findThunderCol(headers, ...keywords) {
    const hl = headers.map(h => (h || '').toLowerCase().trim())
    for (const kw of keywords) {
        const idx = hl.findIndex(h => h.includes(kw.toLowerCase()))
        if (idx >= 0) return idx
    }
    return -1
}

function parseThunderSheet(result) {
    const { format, data, headers, headerRow, hasCommission } = result
    const records = []
    const { companies } = store

    if (format === 'staffing_group') {
        const col = {
            client: findThunderCol(headers, 'Client Name', 'Nombre'),
            travelDate: findThunderCol(headers, 'Travel Date'),
            travelEnd: findThunderCol(headers, 'Travel End'),
            resNum: findThunderCol(headers, 'Reservation'),
            detalle: findThunderCol(headers, 'Detalle'),
            hotel: findThunderCol(headers, 'Nombre del Hotel', 'Hotel'),
            state: findThunderCol(headers, 'Destination', 'State'),
            valor: findThunderCol(headers, 'Valor', 'Total')
        }
        for (let i = headerRow + 1; i < data.length; i++) {
            const row = data[i]
            if (!row) continue
            const name = col.client >= 0 ? String(row[col.client] || '').trim() : ''
            if (!name || name.toLowerCase().includes('total')) continue
            const totalPurchase = col.valor >= 0 ? parseFloat(String(row[col.valor] || '0').replace(/[,$]/g, '')) || 0 : 0
            if (totalPurchase === 0) continue
            const thunderCo = companies.find(c => c.name.toLowerCase().includes('thunder'))
            const commRate = (thunderCo?.defaultCommissionPct || 8.5) / 100
            const commAmt = totalPurchase * commRate
            let checkIn = null, checkOut = null
            if (col.travelDate >= 0) {
                const val = row[col.travelDate]
                if (val instanceof Date || (typeof val === 'number')) {
                    const dt = typeof val === 'number' ? new Date((val - 25569) * 86400 * 1000) : val
                    checkIn = dt.toISOString().split('T')[0]
                } else {
                    const parsed = parseThunderDateRange(String(val || ''))
                    checkIn = parsed.checkIn
                    if (!checkOut) checkOut = parsed.checkOut
                }
            }
            if (col.travelEnd >= 0) {
                const val = row[col.travelEnd]
                if (val instanceof Date || (typeof val === 'number')) {
                    const dt = typeof val === 'number' ? new Date((val - 25569) * 86400 * 1000) : val
                    checkOut = dt.toISOString().split('T')[0]
                } else if (val) {
                    const d = new Date(String(val))
                    if (!isNaN(d) && d.getFullYear() > 2000) checkOut = d.toISOString().split('T')[0]
                }
            }
            records.push({
                travelerName: name, hotelName: col.hotel >= 0 ? String(row[col.hotel] || '').trim() : null,
                destination: col.state >= 0 ? String(row[col.state] || '').trim() : null,
                stateAbbr: col.state >= 0 ? String(row[col.state] || '').trim() : null,
                reservationNum: col.resNum >= 0 ? String(row[col.resNum] || '').trim() : null,
                detail: col.detalle >= 0 ? String(row[col.detalle] || '').trim() : null,
                checkIn, checkOut, totalPurchase,
                commissionRate: 8.5, commissionAmount: commAmt, m2mPct: 70, m2mAmount: commAmt * 0.70,
                sourceFormat: 'staffing_group'
            })
        }
    } else {
        // Expedia A or B
        const col = {
            client: findThunderCol(headers, 'Client Name'),
            guests: findThunderCol(headers, 'Number of Guest'),
            rooms: findThunderCol(headers, 'Number of Room'),
            purchaseDate: findThunderCol(headers, 'Date of purchase'),
            bookingDates: findThunderCol(headers, 'Booking dates'),
            resNum: findThunderCol(headers, 'Reservation Number'),
            hotel: findThunderCol(headers, 'Hotel Name'),
            detail: findThunderCol(headers, 'Booking detail'),
            destination: findThunderCol(headers, 'Destination'),
            total: findThunderCol(headers, 'Total of purchase'),
            commTotal: findThunderCol(headers, 'Comision Total', 'Commission Total'),
            comm70: findThunderCol(headers, 'Comision (70%)', 'Commission (70%)')
        }
        for (let i = headerRow + 1; i < data.length; i++) {
            const row = data[i]
            if (!row) continue
            const name = col.client >= 0 ? String(row[col.client] || '').trim() : ''
            if (!name || name.toLowerCase().includes('total')) continue
            const dest = col.destination >= 0 ? String(row[col.destination] || '').trim() : ''
            if (dest.toLowerCase().includes('total')) continue
            const totalPurchase = col.total >= 0 ? parseFloat(String(row[col.total] || '0').replace(/[,$]/g, '')) || 0 : 0
            if (totalPurchase === 0) continue
            let commAmt = 0
            if (hasCommission && col.comm70 >= 0 && row[col.comm70]) {
                commAmt = parseFloat(String(row[col.comm70] || '0').replace(/[,$]/g, '')) || 0
            } else if (hasCommission && col.commTotal >= 0 && row[col.commTotal]) {
                const fullComm = parseFloat(String(row[col.commTotal] || '0').replace(/[,$]/g, '')) || 0
                commAmt = fullComm * 0.70
            } else {
                commAmt = totalPurchase * 0.085 * 0.70
            }
            const commRate = totalPurchase > 0 ? (commAmt / 0.70 / totalPurchase * 100) : 8.5
            let checkIn = null, checkOut = null
            if (col.bookingDates >= 0 && row[col.bookingDates]) {
                const parsed = parseThunderDateRange(String(row[col.bookingDates]))
                checkIn = parsed.checkIn
                checkOut = parsed.checkOut
            }
            records.push({
                travelerName: name, clientName: name,
                hotelName: col.hotel >= 0 ? String(row[col.hotel] || '').trim() : null,
                destination: dest || null,
                reservationNum: col.resNum >= 0 ? String(row[col.resNum] || '').trim() : null,
                detail: col.detail >= 0 ? String(row[col.detail] || '').trim() : null,
                checkIn, checkOut,
                numGuests: col.guests >= 0 ? parseInt(row[col.guests]) || 1 : 1,
                numRooms: col.rooms >= 0 ? parseInt(row[col.rooms]) || 1 : 1,
                totalPurchase, commissionRate: Math.round(commRate * 100) / 100,
                commissionAmount: commAmt, m2mPct: 70, m2mAmount: commAmt,
                bookingDate: col.purchaseDate >= 0 ? parseThunderPurchaseDate(row[col.purchaseDate]) : null,
                sourceFormat: format
            })
        }
    }
    return records
}

function processThunderImport(sheets, fileName) {
    const { companies, companyBookings } = store
    let allRecords = []
    sheets.forEach(sheet => {
        const records = parseThunderSheet(sheet)
        records.forEach(r => r.sourceFile = fileName + ' / ' + sheet.sheetName)
        allRecords = allRecords.concat(records)
    })

    if (!allRecords.length) { window.toast('No se encontraron reservas en el archivo', 'error'); return }

    // Auto-assign Thunder Staffing company
    const thunderCompany = companies.find(c => c.name.toLowerCase().includes('thunder'))
    const companyId = thunderCompany?.id || thunderCompany?._supabaseId

    // Detect duplicates by reservation number
    const existingNums = new Set(companyBookings.map(b => b.reservationNum).filter(Boolean))
    const newRecords = []
    const duplicates = []
    allRecords.forEach(r => {
        if (r.reservationNum && existingNums.has(r.reservationNum)) duplicates.push(r)
        else newRecords.push(r)
    })

    window.thunderImportData = { allRecords, newRecords, duplicates, companyId, fileName }
    showThunderPreview()
}

function showThunderPreview() {
    const { allRecords, newRecords, duplicates, fileName } = window.thunderImportData
    const totalPurchase = allRecords.reduce((s, r) => s + (r.totalPurchase || 0), 0)
    const totalM2M = allRecords.reduce((s, r) => s + (r.m2mAmount || 0), 0)
    const formats = [...new Set(allRecords.map(r => r.sourceFormat))]
    const formatLabels = { expedia_a: 'Expedia (nuevo)', expedia_b: 'Expedia (antiguo)', staffing_group: 'Staffing Group' }

    // Use the import view's preview area
    const preview = document.getElementById('preview')
    if (!preview) return
    preview.innerHTML = `
        <div style="padding:20px;">
            <h3 style="margin-bottom:12px;">Thunder Staffing Import</h3>
            <div style="font-size:12px;color:var(--text-2);margin-bottom:16px;">
                Archivo: <strong>${window.escHtml(fileName)}</strong><br>
                Formato: <strong>${formats.map(f => formatLabels[f] || f).join(', ')}</strong> | Empresa: <strong>Thunder Staffing</strong>
            </div>
            <div class="emp-stats-row" style="margin-bottom:16px;">
                <div class="emp-stat-card"><div class="emp-stat-val">${allRecords.length}</div><div class="emp-stat-label">Total Reservas</div></div>
                <div class="emp-stat-card"><div class="emp-stat-val" style="color:var(--green);">${newRecords.length}</div><div class="emp-stat-label">Nuevas</div></div>
                <div class="emp-stat-card"><div class="emp-stat-val" style="color:var(--yellow);">${duplicates.length}</div><div class="emp-stat-label">Duplicadas</div></div>
                <div class="emp-stat-card"><div class="emp-stat-val" style="color:var(--blue);">${window.fmtUSD(totalPurchase)}</div><div class="emp-stat-label">Compra Total</div></div>
                <div class="emp-stat-card"><div class="emp-stat-val" style="color:var(--green);">${window.fmtUSD(totalM2M)}</div><div class="emp-stat-label">M2M Total</div></div>
            </div>
            <div style="display:flex;gap:10px;justify-content:flex-end;">
                <button class="btn btn-secondary" onclick="cancelThunderImport()">Cancelar</button>
                ${duplicates.length > 0 ? `<button class="btn" style="background:var(--yellow);color:#000;" onclick="confirmThunderImport(true)">Importar Solo Nuevas (${newRecords.length})</button>` : ''}
                <button class="btn btn-success" onclick="confirmThunderImport(false)">Importar Todo (${allRecords.length})</button>
            </div>
        </div>`
    preview.classList.remove('hidden')
    window.showView('import')
}

function cancelThunderImport() {
    window.thunderImportData = null
    const preview = document.getElementById('preview')
    if (preview) { preview.innerHTML = ''; preview.classList.add('hidden') }
}

function confirmThunderImport(skipDuplicates) {
    const { companyBookings } = store
    const { allRecords, newRecords, companyId } = window.thunderImportData
    const toImport = skipDuplicates ? newRecords : allRecords
    if (!toImport.length) { window.toast('No hay reservas para importar', 'warning'); return }

    toImport.forEach(r => {
        r.companyId = companyId
        r.stage = 'pendiente'
        r.id = 'bk_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5)
    })
    companyBookings.push(...toImport)

    // Navigate to empresas pipeline immediately
    cancelThunderImport()
    window.thunderImportData = null
    store.crmMode = 'empresas'
    document.querySelectorAll('.mode-btn').forEach(btn => btn.classList.toggle('active', btn.dataset.mode === 'empresas'))
    document.querySelectorAll('.nav-turismo').forEach(el => el.style.display = 'none')
    document.querySelectorAll('.nav-empresas').forEach(el => el.style.display = '')
    window.showView('empPipeline')
    window.populateEmpCompanyDropdowns()
    window.renderEmpPipeline()

    // Sync to Supabase in background
    window.supabaseDB.insertBookings(toImport, (batch, total, count) => {
        window.showSyncProgress(count, toImport.length, 'Sincronizando reservas Thunder...')
    }).then(result => {
        if (result) {
            result.forEach(row => {
                const match = toImport.find(b => b.reservationNum === row.reservation_num && !b._supabaseId)
                if (match) { match._supabaseId = row.id; match.id = row.id }
            })
        }
        window.hideSyncProgress()
        window.toast(`${toImport.length} reservas Thunder Staffing importadas`, 'success')
    })
}

// Auto-import Thunder sheets from a mixed Excel (no preview, just import silently)
function autoImportThunderSheets(sheets, fileName) {
    const { companies, companyBookings } = store
    let allRecords = []
    sheets.forEach(sheet => {
        const records = parseThunderSheet(sheet)
        records.forEach(r => r.sourceFile = fileName + ' / ' + sheet.sheetName)
        allRecords = allRecords.concat(records)
    })
    if (!allRecords.length) return

    const thunderCompany = companies.find(c => c.name.toLowerCase().includes('thunder'))
    const companyId = thunderCompany?.id || thunderCompany?._supabaseId

    // Deduplicate against existing bookings by reservation number or name+date
    const existingNums = new Set(companyBookings.map(b => b.reservationNum).filter(Boolean))
    const existingKeys = new Set(companyBookings.map(b => (b.travelerName || '').toLowerCase() + '|' + (b.checkIn || '')).filter(k => k !== '|'))
    const toImport = allRecords.filter(r => {
        if (r.reservationNum && existingNums.has(r.reservationNum)) return false
        if (!r.reservationNum) {
            const key = (r.travelerName || '').toLowerCase() + '|' + (r.checkIn || '')
            if (key !== '|' && existingKeys.has(key)) return false
        }
        return true
    })
    if (!toImport.length) { window.toast(`Staffing: ${allRecords.length} reservas ya existentes (omitidas)`, 'info'); return }

    toImport.forEach(r => {
        r.companyId = companyId
        r.stage = 'pendiente'
        r.id = 'bk_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5)
    })
    companyBookings.push(...toImport)

    // Sync to Supabase in background
    window.supabaseDB.insertBookings(toImport, (batch, total, count) => {
        window.showSyncProgress(count, toImport.length, 'Sincronizando reservas Staffing...')
    }).then(result => {
        if (result) {
            result.forEach(row => {
                const match = toImport.find(b => b.reservationNum === row.reservation_num && !b._supabaseId)
                if (match) { match._supabaseId = row.id; match.id = row.id }
            })
        }
        window.hideSyncProgress()
        window.toast(`${toImport.length} reservas Staffing importadas a Empresas`, 'success')
    })
}

Object.assign(window, {
    detectThunderStaffing,
    parseThunderDateRange,
    thunderDateParts,
    parseThunderPurchaseDate,
    findThunderCol,
    parseThunderSheet,
    processThunderImport,
    showThunderPreview,
    cancelThunderImport,
    confirmThunderImport,
    autoImportThunderSheets
})
